
import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { PlanCard } from '../components/PlanCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Helmet } from 'react-helmet';
import { supabase } from "@/integrations/supabase/client";
import { Plan } from '@/types';

const ProviderPage = () => {
  const { providerSlug } = useParams<{ providerSlug: string }>();
  const [providerName, setProviderName] = useState('');
  const [providerDescription, setProviderDescription] = useState('');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [randomPlans, setRandomPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchProviderData = async () => {
      if (!providerSlug) {
        setNotFound(true);
        return;
      }

      try {
        setIsLoading(true);
        
        // Convert slug back to provider name for querying
        const normalizedProviderName = providerSlug.replace(/-/g, ' ');
        
        // Get provider details and SEO content
        const { data: provider } = await supabase
          .from('providers')
          .select(`
            *,
            provider_seo_pages (
              title,
              content
            )
          `)
          .ilike('name', normalizedProviderName)
          .single();
        
        if (provider) {
          setProviderName(provider.name);
          if (provider.provider_seo_pages) {
            setProviderDescription(provider.provider_seo_pages.content || `<p>Mobilabonnementer fra ${provider.name}</p>`);
          } else {
            setProviderDescription(`<p>Mobilabonnementer fra ${provider.name}</p>`);
          }
        } else {
          setProviderName(normalizedProviderName.charAt(0).toUpperCase() + normalizedProviderName.slice(1));
          setProviderDescription(`<p>Mobilabonnementer fra ${normalizedProviderName}</p>`);
        }
        
        // Fetch plans for this provider
        const { data: providerPlans, error: plansError } = await supabase
          .from('plans')
          .select('*')
          .ilike('provider', normalizedProviderName)
          .order('popularity', { ascending: false });
        
        if (plansError) throw plansError;
        
        if (!providerPlans || providerPlans.length === 0) {
          setNotFound(true);
          return;
        }
        
        // Transform plans to match Plan type
        const transformedPlans: Plan[] = providerPlans.map(plan => ({
          ...plan,
          features: Array.isArray(plan.features) ? plan.features.map(f => String(f)) : []
        }));
        
        setPlans(transformedPlans);
        
        // Fetch plans for other providers
        const { data: otherPlans, error: otherPlansError } = await supabase
          .from('plans')
          .select('*')
          .not('provider', 'ilike', normalizedProviderName)
          .order('popularity', { ascending: false })
          .limit(10);
        
        if (otherPlansError) throw otherPlansError;
        
        // Transform and randomize other plans
        const transformedOtherPlans: Plan[] = otherPlans.map(plan => ({
          ...plan,
          features: Array.isArray(plan.features) ? plan.features.map(f => String(f)) : []
        }));
        
        // Randomly select 3 plans
        const shuffled = [...transformedOtherPlans].sort(() => 0.5 - Math.random());
        setRandomPlans(shuffled.slice(0, 3));
        
      } catch (error) {
        console.error('Error fetching provider data:', error);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProviderData();
  }, [providerSlug]);
  
  if (notFound) {
    return <Navigate to="/404" replace />;
  }
  
  const canonicalUrl = `https://abonemt.dk/provider/${providerSlug}`;
  
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>{`${providerName} Mobilabonnementer | Abonemt`}</title>
        <meta name="description" content={`Find de bedste mobilabonnementer fra ${providerName}. Sammenlign priser, data og fordele.`} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 md:px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
            {providerName} Mobilabonnementer
          </h1>
          
          {/* Provider plans */}
          {plans.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">Abonnementer fra {providerName}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} />
                ))}
              </div>
            </div>
          )}
          
          {/* Provider description/SEO content */}
          <div className="prose prose-lg mx-auto mt-12 mb-12">
            <div dangerouslySetInnerHTML={{ __html: providerDescription }} />
          </div>
          
          {/* Random offers from other providers */}
          {randomPlans.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-semibold mb-4">Andre populære tilbud</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {randomPlans.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProviderPage;
